// WhatsApp Admin API client
import { API_BASE_URL, request, uploadFile } from "./base";

/**
 * Absolute URL for the bytes of one media message.
 *
 * Meta media is not public — the API proxies it behind the platform-admin
 * guard, keyed on the MESSAGE so authorisation follows what the admin can
 * already see in the thread. Built from the shared base like every other
 * portal URL; never hardcode a host.
 */
export function getWaMediaUrl(messageId: number): string {
	return `${API_BASE_URL}/api/admin/whatsapp/messages/${messageId}/media`;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export type WaConversation = {
	id: number;
	phoneNumber: string;
	contactName: string | null;
	contactType: "pro" | "customer" | "unknown";
	proId: string | null;
	/** The pro behind the number, when it matches one. */
	pro: {
		id: string;
		businessName: string;
		status: string;
		whatsapp: string | null;
		email: string | null;
		city: string | null;
		ownerName: string | null;
		ownerEmail: string | null;
	} | null;
	inquiryId: number | null;
	unreadCount: number;
	lastMessageAt: string | null;
	lastCustomerMessageAt: string | null;
	dateCreated: string;
	dateUpdated: string;
};

/**
 * A row in the inbox list. On the Pros tab a pro we have never messaged has no
 * conversation yet, so `id` is null until one is opened.
 */
export type WaInboxRow = Omit<WaConversation, "id" | "dateCreated" | "dateUpdated"> & {
	id: number | null;
	dateCreated: string | null;
	dateUpdated: string | null;
};

export type WaMessage = {
	id: number;
	conversationId: number;
	wamid: string | null;
	direction: "inbound" | "outbound";
	type: string;
	content: string | null;
	templateName: string | null;
	status: string;
	errorCode: string | null;
	campaignId: number | null;
	sentBy: string | null;
	dateCreated: string;
	dateUpdated: string;
};

export type WaTemplate = {
	id: number;
	metaTemplateId: string | null;
	name: string;
	language: string;
	category: string;
	status: string;
	components: string | null;
	rejectionReason: string | null;
	dateCreated: string;
	dateUpdated: string;
};

export type WaCampaign = {
	id: number;
	name: string;
	templateId: number;
	templateParams: string | null;
	status: string;
	scheduledAt: string | null;
	statsTotal: number;
	statsSent: number;
	statsDelivered: number;
	statsRead: number;
	statsFailed: number;
	createdBy: string;
	dateCreated: string;
	dateUpdated: string;
};

export type WaCampaignRecipient = {
	id: number;
	campaignId: number;
	phoneNumber: string;
	contactName: string | null;
	templateParams: string | null;
	status: string;
	wamid: string | null;
	errorCode: string | null;
	dateCreated: string;
	dateUpdated: string;
};

export type WaStats = {
	totalConversations: number;
	unreadCount: number;
	messagesToday: number;
	pendingTemplates: number;
	activeCampaigns: number;
};

export type WaContact = {
	phone: string;
	name: string;
	source: string;
	city?: string;
	requirementType?: string;
};

// ─── API Client ──────────────────────────────────────────────────────────────

export const whatsappApi = {
	// ── Stats ──────────────────────────────────────────────────────────────
	async getStats() {
		return request<WaStats>("/api/admin/whatsapp/stats");
	},

	// ── Conversations ──────────────────────────────────────────────────────
	async getConversations(params?: {
		contactType?: string;
		search?: string;
		/** Contact has written to us at least once. */
		hasCustomerReply?: boolean;
		/** A human on our side has replied. Automated sends do not count. */
		answeredByUs?: boolean;
		limit?: number;
		offset?: number;
	}) {
		const query = params
			? `?${new URLSearchParams(
					Object.entries(params)
						.filter(([, v]) => v !== undefined)
						.map(([k, v]) => [k, String(v)]),
				).toString()}`
			: "";
		return request<{
			conversations: WaInboxRow[];
			total: number;
			limit: number;
			offset: number;
		}>(`/api/admin/whatsapp/conversations${query}`);
	},

	async getConversation(id: number) {
		return request<{
			conversation: WaConversation;
			messages: WaMessage[];
		}>(`/api/admin/whatsapp/conversations/${id}`);
	},

	/** Open the conversation with a pro without sending anything. */
	async startProConversation(proId: string) {
		return request<{ conversation: WaConversation }>(
			"/api/admin/whatsapp/conversations",
			{ method: "POST", body: { proId } },
		);
	},

	async replyToConversation(
		id: number,
		body: {
			message?: string;
			templateName?: string;
			language?: string;
			components?: unknown[];
		},
	) {
		return request<{ message: WaMessage }>(
			`/api/admin/whatsapp/conversations/${id}/reply`,
			{
				method: "POST",
				body,
			},
		);
	},

	async markConversationRead(id: number) {
		return request(`/api/admin/whatsapp/conversations/${id}/read`, {
			method: "POST",
		});
	},

	// ── Templates ──────────────────────────────────────────────────────────
	async getTemplates() {
		return request<{ templates: WaTemplate[] }>(
			"/api/admin/whatsapp/templates",
		);
	},

	async createTemplate(data: {
		name: string;
		language: string;
		category: string;
		components: unknown[];
	}) {
		return request<{ template: WaTemplate }>("/api/admin/whatsapp/templates", {
			method: "POST",
			body: data,
		});
	},

	async updateTemplate(
		id: number,
		data: {
			name?: string;
			language?: string;
			category?: string;
			components?: unknown[];
		},
	) {
		return request<{ template: WaTemplate }>(
			`/api/admin/whatsapp/templates/${id}`,
			{
				method: "PUT",
				body: data,
			},
		);
	},

	async deleteTemplate(id: number) {
		return request(`/api/admin/whatsapp/templates/${id}`, {
			method: "DELETE",
		});
	},

	async syncTemplates() {
		return request<{ synced: number }>("/api/admin/whatsapp/templates/sync", {
			method: "POST",
		});
	},

	// ── Campaigns ──────────────────────────────────────────────────────────
	async getCampaigns(params?: {
		status?: string;
		limit?: number;
		offset?: number;
	}) {
		const query = params
			? `?${new URLSearchParams(
					Object.entries(params)
						.filter(([, v]) => v !== undefined)
						.map(([k, v]) => [k, String(v)]),
				).toString()}`
			: "";
		return request<{ campaigns: WaCampaign[] }>(
			`/api/admin/whatsapp/campaigns${query}`,
		);
	},

	async getCampaign(id: number) {
		return request<{ campaign: WaCampaign }>(
			`/api/admin/whatsapp/campaigns/${id}`,
		);
	},

	async createCampaign(data: {
		name: string;
		templateId: number;
		templateParams?: string;
	}) {
		return request<{ campaign: WaCampaign }>("/api/admin/whatsapp/campaigns", {
			method: "POST",
			body: data,
		});
	},

	async getCampaignRecipients(
		id: number,
		params?: { status?: string; limit?: number; offset?: number },
	) {
		const query = params
			? `?${new URLSearchParams(
					Object.entries(params)
						.filter(([, v]) => v !== undefined)
						.map(([k, v]) => [k, String(v)]),
				).toString()}`
			: "";
		return request<{ recipients: WaCampaignRecipient[] }>(
			`/api/admin/whatsapp/campaigns/${id}/recipients${query}`,
		);
	},

	async addRecipientsManual(
		id: number,
		recipients: Array<{ phone: string; name?: string }>,
	) {
		return request(`/api/admin/whatsapp/campaigns/${id}/recipients/manual`, {
			method: "POST",
			body: { recipients },
		});
	},

	async addRecipientsCSV(id: number, file: File) {
		return uploadFile(
			`/api/admin/whatsapp/campaigns/${id}/recipients/upload`,
			file,
		);
	},

	async addRecipientsFilter(
		id: number,
		filters: {
			source?: string;
			city?: string;
			requirementType?: string;
			dateFrom?: string;
			dateTo?: string;
		},
	) {
		return request(`/api/admin/whatsapp/campaigns/${id}/recipients/filter`, {
			method: "POST",
			body: filters,
		});
	},

	async launchCampaign(id: number) {
		return request(`/api/admin/whatsapp/campaigns/${id}/send`, {
			method: "POST",
		});
	},

	// ── Send ───────────────────────────────────────────────────────────────
	async checkPhone(phone: string) {
		return request<{
			exists: boolean;
			windowOpen: boolean;
			contactName: string | null;
			contactType: string | null;
			canSendText: boolean;
			lastCustomerMessageAt?: string | null;
		}>(
			`/api/admin/whatsapp/send/check-phone?phone=${encodeURIComponent(phone)}`,
		);
	},

	async sendMessage(data: {
		phone: string;
		message?: string;
		templateName?: string;
		language?: string;
		components?: unknown[];
	}) {
		return request<{ message: WaMessage; conversationId: number }>(
			"/api/admin/whatsapp/send",
			{
				method: "POST",
				body: data,
			},
		);
	},

	// ── Contacts ───────────────────────────────────────────────────────────
	async searchContacts(params: {
		source?: string;
		city?: string;
		requirementType?: string;
		dateFrom?: string;
		dateTo?: string;
	}) {
		const query = `?${new URLSearchParams(
			Object.entries(params)
				.filter(([, v]) => v !== undefined)
				.map(([k, v]) => [k, String(v)]),
		).toString()}`;
		return request<{ contacts: WaContact[]; total: number }>(
			`/api/admin/whatsapp/contacts${query}`,
		);
	},
};
